import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { apiClient } from '@/lib/api';
import { useAuthStore } from '@/store/authStore';
import { 
  LoginCredentials, 
  RegisterData, 
  AuthResponse, 
  User 
} from '@/types';
import { ERROR_MESSAGES, SUCCESS_MESSAGES } from '@/lib/constants';

// API endpoints
const AUTH_ENDPOINTS = {
  login: '/auth/login',
  register: '/auth/register',
  logout: '/auth/logout',
  me: '/auth/me',
  refresh: '/auth/refresh',
};

// Keys for query cache
export const authKeys = {
  all: ['auth'] as const,
  user: () => [...authKeys.all, 'user'] as const,
};

/**
 * Hook for user login
 */
export function useLogin() {
  const navigate = useNavigate();
  const login = useAuthStore((state) => state.login);
  const setLoading = useAuthStore((state) => state.setLoading);
  const setError = useAuthStore((state) => state.setError);

  return useMutation({
    mutationFn: async (credentials: LoginCredentials) => {
      setLoading(true);
      try {
        const response = await apiClient.post<AuthResponse>(
          AUTH_ENDPOINTS.login, 
          credentials
        );
        return response;
      } finally {
        setLoading(false);
      }
    },
    onSuccess: (data) => {
      login(data.user, data.accessToken);
      toast.success(`Welcome back, ${data.user.firstName}!`);
      navigate('/dashboard');
    },
    onError: (error: Error) => {
      const message = error.message || 'Invalid email or password';
      setError(message);
      toast.error(message);
    },
  });
}

/**
 * Hook for user registration
 */
export function useRegister() {
  const navigate = useNavigate();
  const login = useAuthStore((state) => state.login);
  const setLoading = useAuthStore((state) => state.setLoading);
  const setError = useAuthStore((state) => state.setError);

  return useMutation({
    mutationFn: async (data: RegisterData) => {
      setLoading(true);
      try {
        const response = await apiClient.post<AuthResponse>(
          AUTH_ENDPOINTS.register, 
          data
        );
        return response;
      } finally {
        setLoading(false);
      }
    },
    onSuccess: (data) => {
      login(data.user, data.accessToken);
      toast.success(SUCCESS_MESSAGES.CREATED);
      navigate('/dashboard');
    },
    onError: (error: Error) => {
      const message = error.message || ERROR_MESSAGES.GENERIC;
      setError(message);
      toast.error(message);
    },
  });
}

/**
 * Hook for user logout
 */
export function useLogout() {
  const navigate = useNavigate();
  const logout = useAuthStore((state) => state.logout);
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      await apiClient.post(AUTH_ENDPOINTS.logout);
    },
    onSuccess: () => {
      logout();
      queryClient.clear();
      navigate('/login');
      toast.success('You have been logged out');
    },
    onError: () => {
      // Still logout on error
      logout();
      queryClient.clear();
      navigate('/login');
    },
  });
}

/**
 * Hook to get current user
 */
export function useCurrentUser() {
  const setUser = useAuthStore((state) => state.setUser);

  return useQuery({
    queryKey: authKeys.user(),
    queryFn: async () => {
      const user = await apiClient.get<User>(AUTH_ENDPOINTS.me);
      setUser(user);
      return user;
    },
    retry: false,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

/**
 * Hook to check if user is authenticated
 */
export function useIsAuthenticated() {
  return useAuthStore((state) => state.isAuthenticated);
}

/**
 * Hook to get auth loading state
 */
export function useAuthLoading() {
  return useAuthStore((state) => state.isLoading);
}

/**
 * Hook to get auth error
 */
export function useAuthError() {
  return useAuthStore((state) => state.error);
}
